import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALTREATMENT_LOAD_BY_ID,
    ACTION_TRIALTREATMENT_LOAD_FAILURE,
    ACTION_TRIALTREATMENT_LOAD_SUCCESS,

    ACTION_TRIALTREATMENT_SAVE,
    ACTION_TRIALTREATMENT_SAVE_SUCCESS,
    ACTION_TRIALTREATMENT_SAVE_FAILURE,

    ACTION_TRIALTREATMENT_CREATE,
    ACTION_TRIALTREATMENT_CREATE_SUCCESS,
    ACTION_TRIALTREATMENT_CREATE_FAILURE,

    ACTION_TRIALTREATMENT_SYNC,
    ACTION_TRIALTREATMENT_SYNC_SUCCESS,
    ACTION_TRIALTREATMENT_SYNC_FAILURE,

    ACTION_TRIALTREATMENT_CLEAR,
    ACTION_TRIALTREATMENT_FILTER,

    TrialTreatmentAction
} from '../actions/trialTreatment';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialTreatmentState {
    formData: Dtos.TrialTreatment | Dtos.TrialTreatment[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialTreatment[];
    formLength: number;
}

const initialState: ITrialTreatmentState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialTreatmentState, isLoading: boolean): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialTreatmentState,
    formData: Dtos.TrialTreatment | Dtos.TrialTreatment[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialTreatmentState, responseStatus: Dtos.ResponseStatus): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialTreatmentState, formFilter: IFormFilter): ITrialTreatmentState {

    const items: Dtos.TrialTreatment[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialTreatmentState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialTreatmentState, propertiesToClear?: string[]): ITrialTreatmentState {

    if (!propertiesToClear) {
        const newState: ITrialTreatmentState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialTreatmentState, isSaving: boolean): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialTreatmentState,
    formData: Dtos.TrialTreatment | Dtos.TrialTreatment[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialTreatmentState, responseStatus: Dtos.ResponseStatus): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialTreatmentState, isCreating: boolean): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialTreatmentState,
    formData: Dtos.TrialTreatment,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialTreatmentState, responseStatus: Dtos.ResponseStatus): ITrialTreatmentState {
    const newState: ITrialTreatmentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialTreatmentReducer: Reducer<ITrialTreatmentState> = (state: ITrialTreatmentState = initialState, action: TrialTreatmentAction) => {
    switch (action.type) {
        case ACTION_TRIALTREATMENT_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALTREATMENT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
        case ACTION_TRIALTREATMENT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALTREATMENT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALTREATMENT_SAVE:
            return saving(state, true);
        case ACTION_TRIALTREATMENT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALTREATMENT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALTREATMENT_CREATE:
            return creating(state, true);
        case ACTION_TRIALTREATMENT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_TRIALTREATMENT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALTREATMENT_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALTREATMENT_SYNC:
            return loading(state, true);
        case ACTION_TRIALTREATMENT_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
        case ACTION_TRIALTREATMENT_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialTreatmentReducer;